import { ZfLoadingSpinner } from '@zafire/react';
import { forwardRef, useEffect } from 'react';
import type {
    CreatePartnerRelationModalProps,
    DeletePartnerRelationModalProps,
    PartnerRelationModalProps,
    PcmModalType,
    UpdatePartnerRelationModalProps,
} from '../../types/PcmPartnerRelationModal';
import useFederatedPartnerDataModule from '../../utils/useFederatedPartnerDataModule';
import { MessageModal } from './MessageModal';

type PcmPartnerRelationModalWidgetProps = PartnerRelationModalProps & {
    modalType: PcmModalType;
    onModalReady: () => void;
};

export const PartnerRelationModalWidget = forwardRef<HTMLElement, PcmPartnerRelationModalWidgetProps>(({ modalType, onModalReady, ...props }, ref) => {
    const { status } = useFederatedPartnerDataModule(modalType);

    useEffect(() => {
        if (modalType === 'CreatePartnerRelation' && ref && 'current' in ref && ref.current) {
            (ref.current as any).createPartnerInput = {
                ...(props as CreatePartnerRelationModalProps),
            };
        }
        if (modalType === 'DeletePartnerRelation' && ref && 'current' in ref && ref.current) {
            (ref.current as any).deletePartnerInput = {
                ...(props as DeletePartnerRelationModalProps),
            };
        }
        if (modalType === 'UpdatePartnerRelation' && ref && 'current' in ref && ref.current) {
            (ref.current as any).updatePartnerInput = {
                ...(props as UpdatePartnerRelationModalProps),
            };
        }
    }, [modalType, props, ref]);

    const getModalJsx = () => {
        switch (modalType) {
            case 'CreatePartnerRelation':
                return <pcm-create-partner-relation-modal ref={ref} />;
            case 'DeletePartnerRelation':
                return <pcm-delete-partner-relation-modal ref={ref} />;
            case 'UpdatePartnerRelation':
                return <pcm-update-partner-relation-modal ref={ref} />;
            default:
                return null;
        }
    };

    useEffect(() => {
        if (status === 'done') onModalReady();
    }, [status, onModalReady]);

    return (
        <div>
            {status === 'loading' ? (
                <ZfLoadingSpinner style={{ margin: 'auto' }} />
            ) : status === 'error' ? (
                <MessageModal message="Failed to load the PCM Partner Relation Modal" />
            ) : (
                getModalJsx()
            )}
        </div>
    );
});
